export function fmtUSD(v: number): string {
  if (v >= 1000) return v.toLocaleString('en-US', { maximumFractionDigits: 0 });
  return v.toFixed(2);
}

export function fmtHKD(v: number): string {
  return v.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

export function fmtPop(n: number | null | undefined): string {
  if (n == null) return '—';
  if (n >= 1000) return n.toLocaleString('en-US');
  return String(n);
}

export function fmtSignedHKD(v: number): string {
  const a = Math.abs(Math.round(v));
  return `${v >= 0 ? '+' : '−'}HK$${a.toLocaleString('en-US')}`;
}

export function fmtPctSigned(v: number): string {
  return `${(v >= 0 ? '+' : '') + v.toFixed(1)}%`;
}

/** Whole-yen, grouped: 45312 → "¥45,312". */
export function fmtJpy(v: number): string {
  return `¥${Math.round(v).toLocaleString('en-US')}`;
}

/** Signed compact yen for tiles: 8457 → "¥+8.5k", -1200 → "¥−1.2k". */
export function fmtJpyK(v: number): string {
  const a = Math.abs(v);
  const s = v < 0 ? '−' : '+';
  return a >= 1000 ? `¥${s}${(a / 1000).toFixed(1)}k` : `¥${s}${a}`;
}

/** Spread fraction → percent. 0.187 → "+19%" (round) or "+18.7%" (precise). */
export function fmtSpreadPct(p: number, precise = false): string {
  const s = p >= 0 ? '+' : '−';
  return `${s}${Math.abs(p * 100).toFixed(precise ? 1 : 0)}%`;
}

/** JPY → HKD via USD intermediate. Falls back to ¥150/USD if arb FX isn't
 *  loaded yet (so the panel doesn't render NaN). */
export function jpyToHkd(jpy: number, fx: { usd_hkd: number; jpy_usd?: number | null }): number {
  const jpyPerUsd = fx.jpy_usd ?? 150;
  return (jpy / jpyPerUsd) * fx.usd_hkd;
}

/** JPY → "HK$1,234". Sign-preserving. */
export function fmtJpyAsHkd(
  jpy: number,
  fx: { usd_hkd: number; jpy_usd?: number | null },
): string {
  const hkd = jpyToHkd(jpy, fx);
  const sign = hkd < 0 ? '−' : '';
  return `${sign}HK$${Math.abs(Math.round(hkd)).toLocaleString('en-US')}`;
}

/** JPY → signed compact HKD for tile-level display. */
export function fmtJpyAsHkdK(
  jpy: number,
  fx: { usd_hkd: number; jpy_usd?: number | null },
): string {
  const hkd = jpyToHkd(jpy, fx);
  const a = Math.abs(hkd);
  const s = hkd < 0 ? '−' : '+';
  return a >= 1000 ? `HK$${s}${(a / 1000).toFixed(1)}k` : `HK$${s}${Math.round(a)}`;
}

/**
 * Display label for a signal. Underlying server enum stays 'BUY' (DB rows,
 * API payloads, CSS hooks); only the user-visible string changes here.
 * Operator preference — "HOT" reads less like a trade instruction.
 */
export function labelForSignal(s: 'BUY' | 'SELL' | 'HOLD'): string {
  return s === 'BUY' ? 'HOT' : s;
}

/**
 * Convert a gross USD price into the HKD value the buyer's Snkrdunk app would
 * show: subtract the flat coupon, then multiply by the effective FX. Coupon
 * and FX come from /api/fx so server is the single source of truth.
 *
 * Pass the FxRate object directly when you have it; this keeps callers from
 * accidentally passing only `usd_hkd` (which would skip the coupon and
 * re-introduce the bug this helper exists to fix).
 */
export function usdToHkdDisplay(
  usd: number,
  fx: { usd_hkd: number; coupon_usd_cents: number },
): number {
  const net = Math.max(0, usd - fx.coupon_usd_cents / 100);
  return net * fx.usd_hkd;
}

/** Default FX values for components rendered before /api/fx resolves. */
export const FX_FALLBACK = { usd_hkd: 7.72, coupon_usd_cents: 1000 } as const;

export function todayLocalISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function fmtDateShort(s: string): string {
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return `${d.getDate()} ${months[d.getMonth()]}`;
}
